import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Plus, Check } from 'lucide-react'

interface ComboBoxProps {
  value: string
  onChange: (value: string, isNew: boolean) => void
  options: string[]
  placeholder?: string
  label?: string
  required?: boolean
  allowNew?: boolean
  newItemLabel?: string
  icon?: React.ReactNode
}

export default function ComboBox({
  value,
  onChange,
  options,
  placeholder = 'Select or type...',
  label,
  required = false,
  allowNew = true,
  newItemLabel = 'Add new',
  icon
}: ComboBoxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    // Filter options based on input
    const filtered = options.filter(opt => 
      opt.toLowerCase().includes(inputValue.toLowerCase())
    )
    setFilteredOptions(filtered)
  }, [inputValue, options])

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    setIsOpen(true)
    onChange(newValue, !options.includes(newValue))
  }

  const handleSelect = (option: string) => {
    setInputValue(option)
    onChange(option, false)
    setIsOpen(false)
  }

  const handleAddNew = () => {
    if (inputValue.trim()) {
      onChange(inputValue.trim(), true)
      setIsOpen(false)
    }
  }

  const isNewValue = inputValue.trim() && !options.some(
    opt => opt.toLowerCase() === inputValue.toLowerCase()
  )

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && '*'}
        </label>
      )}
      <div className="relative">
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            {icon}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          required={required}
          className={`input-field ${icon ? 'pl-12' : ''} pr-10`}
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen)
            inputRef.current?.focus()
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-primary-200 max-h-60 overflow-auto">
          {/* Existing options */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full px-4 py-3 text-left hover:bg-primary-50 flex items-center justify-between transition-colors ${
                  option === value ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                }`}
              >
                <span>{option}</span>
                {option === value && <Check className="w-4 h-4 text-primary-600" />}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-gray-500 text-sm">
              No matching options
            </div>
          )}

          {/* Add new option */}
          {allowNew && isNewValue && (
            <>
              <div className="border-t border-primary-100" />
              <button
                type="button"
                onClick={handleAddNew}
                className="w-full px-4 py-3 text-left hover:bg-green-50 flex items-center gap-2 text-green-700 font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                {newItemLabel}: &quot;{inputValue}&quot;
              </button>
            </>
          )}
        </div>
      )}

      {/* New value indicator */}
      {isNewValue && inputValue && !isOpen && (
        <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
          <Plus className="w-3 h-3" />
          New {label?.toLowerCase() || 'value'} will be created
        </p>
      )}
    </div>
  )
}

